// =====================================================
// CONDITION BUILDER
// Stage 2: Participants & Pricing
// Always-visible criteria rows for a pricing rule (all must match)
// =====================================================

import { AlertTriangle, Plus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SegmentedControl } from '@/components/ui/segmented-control';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AUTHORITY_ROLE_OPTIONS,
  PARTICIPANT_TYPE_OPTIONS,
  formatCondition,
} from '@/models/pricingRule';
import { DRAFT_KINDS, draftProblem, type ConditionDraft, type DraftKind } from '../utils/conditionDrafts';
import { OptionMultiSelect, type MultiSelectOption } from './OptionMultiSelect';
import type { Degree } from '@/models/degree';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';

interface ConditionBuilderProps {
  drafts: ConditionDraft[];
  degrees: Degree[];
  districts: District[];
  regions: Region[];
  isLoadingData: boolean;
  /** show per-row problems (after a failed submit) */
  showProblems: boolean;
  onAdd: () => void;
  onUpdate: (key: string, patch: Partial<ConditionDraft>) => void;
  onRemove: (key: string) => void;
}

export function ConditionBuilder({
  drafts,
  degrees,
  districts,
  regions,
  isLoadingData,
  showProblems,
  onAdd,
  onUpdate,
  onRemove,
}: ConditionBuilderProps) {
  const usedKinds = new Set(drafts.map((d) => d.kind).filter(Boolean));
  const canAddMore = usedKinds.size < DRAFT_KINDS.length;

  // Regions follow the districts chosen in another row (when any)
  const pickedDistricts = drafts.find((d) => d.kind === 'DISTRICT' && !d.negate)?.values ?? [];
  const regionPool = pickedDistricts.length ? regions.filter((r) => pickedDistricts.includes(r.district_id)) : regions;

  const optionsFor = (kind: DraftKind): MultiSelectOption[] => {
    switch (kind) {
      case 'PARTICIPANT_TYPE':
        return PARTICIPANT_TYPE_OPTIONS;
      case 'MINISTER_DEGREE':
        return degrees.map((d) => ({ value: d.id, label: d.name }));
      case 'DISTRICT':
        return districts.map((d) => ({ value: d.id, label: d.name ?? d.code ?? d.id }));
      case 'REGION':
        return regionPool.map((r) => ({ value: r.id, label: r.name ?? r.code ?? r.id }));
      case 'ROLE':
        return AUTHORITY_ROLE_OPTIONS;
      default:
        return [];
    }
  };

  const placeholderFor: Partial<Record<DraftKind, string>> = {
    PARTICIPANT_TYPE: 'Elegir tipo(s)…',
    MINISTER_DEGREE: 'Elegir grado(s)…',
    DISTRICT: 'Elegir distrito(s)…',
    REGION: 'Elegir región(es)…',
    ROLE: 'Elegir cargo(s)…',
  };

  const renderEditor = (d: ConditionDraft) => {
    switch (d.kind) {
      case undefined:
        return null;
      case 'MINISTER_AGE':
        return (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>Entre</span>
              <Input
                type="number"
                min="0"
                step="1"
                className="w-20"
                placeholder="mín."
                value={d.min}
                onChange={(e) => onUpdate(d.key, { min: e.target.value })}
              />
              <span>y</span>
              <Input
                type="number"
                min="0"
                step="1"
                className="w-20"
                placeholder="máx."
                value={d.max}
                onChange={(e) => onUpdate(d.key, { max: e.target.value })}
              />
              <span>años</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Edad al inicio del evento. Puedes dejar un extremo vacío (ej. solo mínima = «60 años o más»).
            </p>
          </div>
        );
      case 'REGISTERED_BEFORE':
        return (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>Se inscribe hasta el</span>
              <Input
                type="date"
                className="w-44"
                value={d.date}
                onChange={(e) => onUpdate(d.key, { date: e.target.value })}
              />
            </div>
            <p className="text-xs text-muted-foreground">Ese día se incluye. Se usa la fecha de inscripción.</p>
          </div>
        );
      case 'PARTICIPANT_COUNT':
        return (
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span>Los primeros</span>
              <Input
                type="number"
                min="1"
                step="1"
                className="w-24"
                value={d.count}
                onChange={(e) => onUpdate(d.key, { count: e.target.value })}
              />
              <span>inscritos</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Cuenta por orden de inscripción; las inscripciones rechazadas no cuentan.
            </p>
          </div>
        );
      default:
        return (
          <div className="space-y-2">
            <SegmentedControl
              options={[
                { value: 'IS', label: 'Es' },
                { value: 'IS_NOT', label: 'No es' },
              ]}
              value={d.negate ? 'IS_NOT' : 'IS'}
              onChange={(v) => onUpdate(d.key, { negate: v === 'IS_NOT' })}
            />
            <OptionMultiSelect
              options={optionsFor(d.kind)}
              value={d.values}
              onChange={(values) => onUpdate(d.key, { values })}
              placeholder={placeholderFor[d.kind] ?? 'Elegir…'}
              disabled={isLoadingData && ['MINISTER_DEGREE', 'DISTRICT', 'REGION'].includes(d.kind)}
            />
            {d.kind === 'REGION' && pickedDistricts.length > 0 && (
              <p className="text-xs text-muted-foreground">Mostrando solo regiones de los distritos elegidos.</p>
            )}
            {d.values.length > 1 && (
              <p className="text-xs text-muted-foreground">
                {d.negate ? 'Ninguno de los elegidos.' : 'Basta con que sea uno de los elegidos.'}
              </p>
            )}
          </div>
        );
    }
  };

  return (
    <div className="space-y-3">
      {drafts.map((d, index) => {
        const problem = showProblems ? draftProblem(d) : null;
        return (
          <div key={d.key} className="space-y-3 rounded-lg border bg-card p-3">
            <div className="flex items-start gap-2">
              <div className="flex-1 space-y-3">
                {d.legacy ? (
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">Condición antigua</Badge>
                      <span className="text-sm">{formatCondition(d.legacy)}</span>
                    </div>
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                      <AlertTriangle className="size-3" />
                      Esta condición ya no se puede editar. Quítala y agrega el criterio equivalente si hace falta.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">
                        {index === 0 ? 'Criterio' : 'y además…'}
                      </Label>
                      <Select
                        value={d.kind ?? ''}
                        onValueChange={(kind) =>
                          onUpdate(d.key, { kind: kind as DraftKind, negate: false, values: [], min: '', max: '', date: '', count: '' })
                        }
                      >
                        <SelectTrigger className={problem && !d.kind ? 'border-destructive' : ''}>
                          <SelectValue placeholder="Elegir criterio…" />
                        </SelectTrigger>
                        <SelectContent>
                          {DRAFT_KINDS.map((k) => (
                            <SelectItem key={k.kind} value={k.kind} disabled={usedKinds.has(k.kind) && k.kind !== d.kind}>
                              <span>{k.label}</span>
                              <span className="ml-2 text-xs text-muted-foreground">{k.hint}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {renderEditor(d)}
                  </>
                )}
                {problem && <p className="text-sm text-destructive">{problem}</p>}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onRemove(d.key)}
                className="size-8 shrink-0"
                title="Quitar criterio"
              >
                <X className="size-4" />
              </Button>
            </div>
          </div>
        );
      })}

      {canAddMore && (
        <Button type="button" variant="outline" size="sm" onClick={onAdd} className="w-full">
          <Plus className="mr-2 size-4" />
          {drafts.length === 0 ? 'Limitar a ciertos participantes' : 'Agregar otro criterio'}
        </Button>
      )}

      {drafts.length === 0 ? (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          Sin criterios, esta regla se aplica a <strong>todos</strong> los participantes.
        </p>
      ) : (
        drafts.length > 1 && (
          <p className="text-xs text-muted-foreground">Deben cumplirse <strong>todos</strong> los criterios a la vez.</p>
        )
      )}
    </div>
  );
}
